import type {
  SiteLiveData,
  EnergyFlowDataPoint,
  BatteryPlanDataPoint,
  BatteryMode,
} from '../types';

// Generate mock live data for a site
export function generateMockLiveData(): SiteLiveData {
  const modes: BatteryMode[] = [
    'solar_charge',
    'discharging',
    'grid_charge',
    'maximum_sell',
    'curtail',
  ];
  const randomMode = modes[Math.floor(Math.random() * modes.length)];

  const solarPower = 3000 + Math.random() * 4000; // 3000-7000W
  const loadPower = 800 + Math.random() * 1500; // 800-2300W
  const batterySoc = 40 + Math.random() * 50; // 40-90%
  const batteryPower = (Math.random() - 0.5) * 4000; // -2000 to 2000W

  return {
    timestamp: new Date().toISOString(),
    solar: {
      power_w: solarPower,
      daily_kwh: 15 + Math.random() * 25,
    },
    load: {
      power_w: loadPower,
      daily_kwh: 8 + Math.random() * 12,
    },
    battery: {
      soc_percent: batterySoc,
      power_w: batteryPower,
      capacity_kwh: 23,
      mode: randomMode,
    },
    grid: {
      power_w: loadPower - solarPower - batteryPower,
      import_price: 0.1 + Math.random() * 0.4,
      export_price: 0.02 + Math.random() * 0.1,
    },
    forecast: {
      this_hour_wh: 4000 + Math.random() * 2000,
      next_hour_wh: 3500 + Math.random() * 2500,
      remaining_today_kwh: 10 + Math.random() * 15,
      today_total_kwh: 35 + Math.random() * 20,
      tomorrow_kwh: 30 + Math.random() * 25,
    },
    optimizer: {
      mode: randomMode,
      description: getOptimizeDescription(randomMode),
      battery_signal_w: batteryPower,
      grid_signal_w: loadPower - solarPower - batteryPower,
      interval_balance_wh: (Math.random() - 0.5) * 500,
      interval_progress_percent: Math.random() * 100,
      billing_interval_minutes: 5,
    },
  };
}

function getOptimizeDescription(mode: BatteryMode): string {
  const descriptions: Record<BatteryMode, string> = {
    idle: 'Battery is idle, waiting for optimal conditions',
    charging: 'Battery is charging from available sources',
    discharging: 'Battery is discharging to power loads',
    grid_charge: 'Charging from grid during low price period',
    solar_charge: 'Charging from excess solar production',
    extreme_buy: 'Negative prices! Maximizing grid import',
    maximum_sell: 'High prices! Maximizing export to grid',
    curtail: 'Managing solar curtailment',
    curtail_boost: 'Boost charging to prevent curtailment',
  };
  return descriptions[mode] || 'Optimizing battery usage';
}

// Helper to round time down to nearest 30-minute interval
function roundToHalfHour(date: Date): Date {
  const rounded = new Date(date);
  rounded.setMinutes(Math.floor(rounded.getMinutes() / 30) * 30);
  rounded.setSeconds(0);
  rounded.setMilliseconds(0);
  return rounded;
}

// Generate mock energy flow data for the past 24 hours
export function generateMockEnergyFlowData(): EnergyFlowDataPoint[] {
  const data: EnergyFlowDataPoint[] = [];
  const now = roundToHalfHour(new Date());

  for (let i = 47; i >= 0; i--) {
    const timestamp = new Date(now.getTime() - i * 30 * 60 * 1000); // 30-minute intervals
    const hour = timestamp.getHours();

    // Simulate solar curve (peaks around noon)
    const solarFactor = Math.max(0, Math.sin(((hour - 6) / 12) * Math.PI));
    const production = solarFactor * (3000 + Math.random() * 2000);

    // Simulate load pattern (higher in morning and evening)
    const loadFactor = 0.3 + 0.4 * Math.sin(((hour - 8) / 8) * Math.PI) + 0.3 * Math.sin(((hour - 18) / 6) * Math.PI);
    const consumption = 500 + loadFactor * 1500 + Math.random() * 300;

    // Calculate import/export based on production vs consumption
    const netGrid = consumption - production;
    const importWh = Math.max(0, netGrid) * 0.5; // 30-minute energy
    const exportWh = Math.max(0, -netGrid) * 0.5;

    data.push({
      timestamp: timestamp.toISOString(),
      import_wh: importWh,
      consumption_wh: consumption * 0.5,
      production_wh: production * 0.5,
      export_wh: exportWh,
    });
  }

  return data;
}

// Generate mock battery plan data for the next 24 hours
export function generateMockBatteryPlanData(): BatteryPlanDataPoint[] {
  const data: BatteryPlanDataPoint[] = [];
  const now = roundToHalfHour(new Date());
  let currentSoc = 60 + Math.random() * 20; // Start at 60-80%

  for (let i = 0; i < 48; i++) {
    const timestamp = new Date(now.getTime() + i * 30 * 60 * 1000); // 30-minute intervals
    const hour = timestamp.getHours();

    // Simulate solar curve (peaks around noon, 0 at night)
    const solarFactor = hour >= 6 && hour <= 18 ? Math.max(0, Math.sin(((hour - 6) / 12) * Math.PI)) : 0;
    const pvPower = solarFactor * (4000 + Math.random() * 2000); // 0-6kW peak

    // Simulate price curve (high in evening, low overnight)
    const priceFactor = 0.5 + 0.5 * Math.sin(((hour - 6) / 12) * Math.PI);
    const importPrice = 0.08 + priceFactor * 0.35 + (Math.random() - 0.5) * 0.05;
    const exportPrice = importPrice * 0.3 + Math.random() * 0.03;

    // Simulate battery power based on price and solar
    // Charge when prices low, discharge when high
    let batteryPower: number;
    let gridPower: number;
    if (importPrice < 0.15) {
      // Low price - charge from grid (BUY window)
      batteryPower = -(2000 + Math.random() * 1000);
      gridPower = Math.abs(batteryPower) + 500; // Importing to charge + base load
      currentSoc = Math.min(100, currentSoc + 2);
    } else if (importPrice > 0.35) {
      // High price - discharge to grid (SELL window)
      batteryPower = 2000 + Math.random() * 2000;
      gridPower = -(batteryPower - 1000); // Exporting (battery discharge - load)
      currentSoc = Math.max(10, currentSoc - 2);
    } else {
      // Normal - small adjustments (self-consumption)
      batteryPower = (Math.random() - 0.5) * 1000;
      gridPower = (Math.random() - 0.5) * 500; // Small grid exchange
      currentSoc = Math.max(10, Math.min(100, currentSoc + (batteryPower < 0 ? 0.5 : -0.5)));
    }

    data.push({
      timestamp: timestamp.toISOString(),
      battery_power_w: batteryPower,
      grid_power_w: gridPower,
      pv_power_w: pvPower,
      soc_percent: currentSoc,
      import_price: importPrice,
      export_price: exportPrice,
    });
  }

  return data;
}
